'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetProposalsRow, ItemsRenderProps, useGetProposalsInfinite } from '@morfi/backend-sdk-v2/client';
import { useGetProposalsField, useMarketConfig } from '@morfi/backend-sdk-v2/client';
import { AnimatePresence, domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Loading03Icon } from '@/components/ui/icon/glyphs';
import { Panel } from '@/components/ui/panel';
import { deriveTopBackers, type TopBackerSummary } from '@/lib/launchpad/derive-fake-fields';
import { MOTION_ENTRANCE } from '@/lib/ui/easing';
import { PROPOSAL_FEED_LIST_PANEL_FILL_CLASS } from '@/lib/ui/layout';
import { cn } from '@/lib/ui/utils';
import { adaptProposalRow } from './adapt-proposal-row';
import { ProposalEmptyState } from './proposal-empty-state';
import { ProposalFeedListHeader } from './proposal-feed-list-header';
import { ProposalFeedRow } from './proposal-feed-row';
import { ProposalFeedRowSkeleton } from './proposal-feed-row-skeleton';
import type { ProposalScore } from './proposal-score-badge';
import type { ProposalListItem } from './types';
type ProposalsInfiniteQuery = ReturnType<typeof useGetProposalsInfinite>;

/** Matches populated list: mobile &lt; md · 2-col cards md–lg · table lg+ */
const FEED_LIST_UL_CLASS = 'grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-2 lg:grid-cols-1 lg:gap-2' as const;

// ---------------------------------------------------------------------------
// SDK row → legacy ProposalListItem adapter
// ---------------------------------------------------------------------------
//
// The responsive ProposalFeedRow takes the legacy `ProposalListItem` shape
// with flat `proposer*` fields. The SDK returns a nested `proposer:
// UserIdentity` plus feed `topBackers` identities, so this adapter keeps that
// compatibility boundary outside the presentational row.

function adaptTopBackers(row: GetProposalsRow): TopBackerSummary[] {
  const list = Array.isArray(row.topBackers) ? row.topBackers : [];
  const backers = list.map(entry => {
    const backer = entry.backer;
    return {
      walletAddress: backer?.walletAddress ?? '',
      displayName: backer?.displayName ?? null,
      avatarUrl: backer?.avatarUrl ?? null,
      avatarNsfw: backer?.avatarNsfw === true
    };
  });
  return backers.length > 0 ? backers : deriveTopBackers(row.pubkey ?? '');
}

/**
 * Per-row leaf — lives inside `<query.Items>` so it can read live fields via
 * `useGetProposalsField`. Volatile fields (vote counts, backer count, total
 * funded, status) come from the cache leaves so NATS realtime patches flip
 * the UI immediately without re-rendering the whole feed. Static fields
 * (title, image, proposer identity, outcomes) ride on the snapshot row prop.
 *
 * The `<Items>` row ref sits on the plain outer `<li>` (it MUST NOT sit on a
 * motion component — see the body comment / FQ-151). An INNER `<m.div>` carries
 * the entrance/exit animation:
 *   - new prepended rows (graduation onto the Graduated tab) drop in from
 *     y=-16 + opacity 0 with `MOTION_ENTRANCE` (200 ms);
 *   - removed rows (graduation off the Popular/Newest tab via the SDK's
 *     `removeRow:` recipe) play a brief success-tinted ring pulse before
 *     collapsing — see the exit keyframes below.
 * Sibling rows shuffle to fill the gap via the SDK row-animator's FLIP `layout`
 * wrapper around every row, so the inner element no longer needs its own.
 *
 * `useReducedMotion()` gate disables the keyframes for users who opted out.
 */
function ProposalRowLeaf(t0) {
  const $ = _c(38);
  const {
    pubkey,
    elementRef,
    snapshot,
    priorityImage,
    animateValuesOnMount
  } = t0;
  const reduceMotion = useReducedMotion() === true;
  const liveUpvotes = useGetProposalsField(pubkey, "upvotes");
  const liveDownvotes = useGetProposalsField(pubkey, "downvotes");
  const liveNetVotes = useGetProposalsField(pubkey, "netVotes");
  const liveMyVote = useGetProposalsField(pubkey, "myVote");
  const liveBackerCount = useGetProposalsField(pubkey, "numBackers");
  const liveTotalFunded = useGetProposalsField(pubkey, "totalFunded");
  const liveTotalStaked = useGetProposalsField(pubkey, "totalStaked");
  const liveStatus = useGetProposalsField(pubkey, "status");
  const liveScore = useGetProposalsField(pubkey, "proposalScore");
  const config = useMarketConfig(snapshot.marketConfig);
  let t1;
  if ($[0] !== config || $[1] !== snapshot) {
    t1 = adaptProposalRow(snapshot, config);
    $[0] = config;
    $[1] = snapshot;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const base = t1;
  const t2 = liveUpvotes ?? base.upvotes ?? 0;
  const t3 = liveDownvotes ?? base.downvotes ?? 0;
  const t4 = liveNetVotes ?? (liveUpvotes !== undefined && liveDownvotes !== undefined ? liveUpvotes - liveDownvotes : base.netVotes ?? 0);
  const t5 = (liveMyVote !== undefined ? liveMyVote : base.myVote ?? null) as 'up' | 'down' | null;
  const t6 = liveBackerCount ?? base.backerCount;
  const t7 = liveTotalFunded ?? base.totalFunded;
  const t8 = liveTotalStaked ?? base.totalStaked;
  const t9 = liveStatus ?? base.status;
  let t10;
  if ($[3] !== base || $[4] !== t2 || $[5] !== t3 || $[6] !== t4 || $[7] !== t5 || $[8] !== t6 || $[9] !== t7 || $[10] !== t8 || $[11] !== t9) {
    t10 = {
      ...base,
      upvotes: t2,
      downvotes: t3,
      netVotes: t4,
      myVote: t5,
      backerCount: t6,
      totalFunded: t7,
      totalStaked: t8,
      status: t9
    };
    $[3] = base;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const rowProposal = t10;
  const score = (liveScore ?? snapshot.proposalScore) as ProposalScore ?? "medium";
  let t11;
  if ($[13] !== snapshot) {
    t11 = adaptTopBackers(snapshot);
    $[13] = snapshot;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const top = t11;
  const t12 = elementRef as unknown as React.Ref<HTMLLIElement>;
  let t13;
  if ($[15] !== reduceMotion) {
    t13 = reduceMotion ? false : {
      y: -16,
      opacity: 0
    };
    $[15] = reduceMotion;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      y: 0,
      opacity: 1
    };
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  let t16;
  if ($[18] !== reduceMotion) {
    t15 = reduceMotion ? {
      opacity: 0
    } : {
      opacity: [1, 1, 0],
      scale: [1, 1.015, 0.985],
      boxShadow: ["0 0 0 0 var(--feedback-success-accent)", "0 0 0 4px var(--feedback-success-surface)", "0 0 0 0 var(--feedback-success-accent)"],
      transition: {
        duration: 0.45,
        times: [0, 0.35, 1],
        ease: "easeOut"
      }
    };
    t16 = reduceMotion ? {
      duration: 0
    } : MOTION_ENTRANCE;
    $[18] = reduceMotion;
    $[19] = t15;
    $[20] = t16;
  } else {
    t15 = $[19];
    t16 = $[20];
  }
  let t17;
  if ($[21] !== reduceMotion) {
    t17 = reduceMotion ? {} : {
      style: {
        originY: 0
      }
    };
    $[21] = reduceMotion;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== animateValuesOnMount || $[24] !== priorityImage || $[25] !== rowProposal || $[26] !== score || $[27] !== top) {
    t18 = <ProposalFeedRow proposal={rowProposal} proposalScore={score} topBackers={top} priorityImage={priorityImage} animateValuesOnMount={animateValuesOnMount} />;
    $[23] = animateValuesOnMount;
    $[24] = priorityImage;
    $[25] = rowProposal;
    $[26] = score;
    $[27] = top;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t13 || $[30] !== t15 || $[31] !== t16 || $[32] !== t17 || $[33] !== t18) {
    t19 = <m.div initial={t13} animate={t14} exit={t15} transition={t16} {...t17}>{t18}</m.div>;
    $[29] = t13;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t12 || $[36] !== t19) {
    t20 = <li ref={t12} className="min-w-0">{t19}</li>;
    $[35] = t12;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  return t20;
}

/**
 * Ref-bearing placeholder mounted when a row's `id` (from the subscribed
 * `<Items>`/`useListIds` id-set) has no snapshot yet in the `query.rows`-built
 * map — e.g. a `morfi.market.created.v0` realtime-prepended proposal the parent
 * hasn't re-rendered for. Rendering this (instead of `return null`) keeps the
 * `<m.li ref>` mounted so the SDK attaches the proposal's viewport observer +
 * NATS subs immediately; `ProposalRowLeaf` swaps in on the next render once the
 * snapshot lands. The whole class of "row exists in the id-set but never
 * registers realtime" bugs collapses if the ref ALWAYS attaches. See FQ-150.
 */
function ProposalRowPlaceholder(t0) {
  const $ = _c(3);
  const {
    elementRef
  } = t0;
  const t1 = elementRef as unknown as React.Ref<HTMLLIElement>;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ProposalFeedRowSkeleton />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== t1) {
    t3 = <li ref={t1} className="min-w-0">{t2}</li>;
    $[1] = t1;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
interface ProposalFeedListProps {
  query: ProposalsInfiniteQuery;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

/**
 * Presentational list surface for the proposal feed: error / empty / populated
 * states inside the launchpad `Panel`. Owns the per-pubkey dedup walk so the
 * `<query.Items>` render-prop can look rows up in O(1); volatile fields reach
 * each row via the per-field leaves in `ProposalRowLeaf`.
 */
export function ProposalFeedList(t0) {
  const $ = _c(12);
  const {
    query,
    hasActiveFilters,
    onClearFilters
  } = t0;
  const t = useTranslations("launchpad.feed");
  const sourceRows = query.rows;
  let allProposalRowIndexByPubkey;
  let allProposalRowsByPubkey;
  if ($[0] !== sourceRows) {
    allProposalRowsByPubkey = new Map();
    allProposalRowIndexByPubkey = new Map();
    for (const r of sourceRows) {
      const pubkey = r.pubkey ?? "";
      if (pubkey.length === 0 || allProposalRowsByPubkey.has(pubkey)) {
        continue;
      }
      allProposalRowIndexByPubkey.set(pubkey, allProposalRowsByPubkey.size);
      allProposalRowsByPubkey.set(pubkey, r);
    }
    $[0] = sourceRows;
    $[1] = allProposalRowIndexByPubkey;
    $[2] = allProposalRowsByPubkey;
  } else {
    allProposalRowIndexByPubkey = $[1];
    allProposalRowsByPubkey = $[2];
  }
  const totalProposals = query.ids.length;
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(PROPOSAL_FEED_LIST_PANEL_FILL_CLASS, "rounded-[calc(var(--radius-card)+0.75rem)] pb-3 pt-0", "max-lg:contents");
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== allProposalRowIndexByPubkey || $[5] !== allProposalRowsByPubkey || $[6] !== hasActiveFilters || $[7] !== onClearFilters || $[8] !== query || $[9] !== t || $[10] !== totalProposals) {
    t2 = <Panel surface="solid" inheritBackground={true} className={t1} data-testid="proposal-feed-list-panel">{query.isError ? <div className="flex flex-col items-center justify-center py-24 text-center"><h2 className="text-heading mb-4">{t("errorTitle")}</h2><AnimatedButton variant="secondary" onClick={() => {
          query.refetch();
        }}>{t("errorRetry")}</AnimatedButton></div> : totalProposals === 0 && !hasActiveFilters ? <ProposalEmptyState variant="no-proposals" /> : totalProposals === 0 && hasActiveFilters ? <ProposalEmptyState variant="no-results" onClearFilters={onClearFilters} /> : <><div className="flex flex-col gap-0"><ProposalFeedListHeader /><LazyMotion features={domAnimation}><ul className={FEED_LIST_UL_CLASS}><AnimatePresence initial={false}><query.Items>{t3 => {
                    const {
                      ref,
                      id
                    } = t3;
                    const row = allProposalRowsByPubkey.get(id);
                    if (row === undefined) {
                      return <ProposalRowPlaceholder key={id} elementRef={ref} />;
                    }
                    const idx = allProposalRowIndexByPubkey.get(id) ?? 0;
                    return <ProposalRowLeaf key={id} pubkey={id} elementRef={ref} snapshot={row} priorityImage={idx < 6} animateValuesOnMount={query.isFetchedAfterMount} />;
                  }}</query.Items></AnimatePresence></ul></LazyMotion></div>{query.hasNextPage && query.isFetchingNextPage && <div className="flex justify-center py-4"><Icon icon={Loading03Icon} className="size-6 animate-spin text-foreground-secondary" /></div>}</>}</Panel>;
    $[4] = allProposalRowIndexByPubkey;
    $[5] = allProposalRowsByPubkey;
    $[6] = hasActiveFilters;
    $[7] = onClearFilters;
    $[8] = query;
    $[9] = t;
    $[10] = totalProposals;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}